import { invoke } from '@tauri-apps/api/core';

export interface AuthStorageUser {
  id: string;
  name: string;
  email: string;
}

export interface AuthStorageSession {
  token: string;
  user: AuthStorageUser;
  activeWorkspaceId: string | null;
}

export interface ClearAuthOptions {
  deleteLocalActivityData?: boolean;
}

export interface AuthStorage {
  hydrate(): Promise<AuthStorageSession | null>;
  save(session: AuthStorageSession): Promise<void>;
  setActiveWorkspaceId(activeWorkspaceId: string | null): Promise<void>;
  clear(options?: ClearAuthOptions): Promise<void>;
}

const TOKEN_KEY = 'daysheetly_token';
const USER_KEY = 'daysheetly_user';
const ACTIVE_WORKSPACE_KEY = 'daysheetly_active_workspace_id';

export const webAuthStorage: AuthStorage = {
  async hydrate() {
    const token = localStorage.getItem(TOKEN_KEY);
    const serializedUser = localStorage.getItem(USER_KEY);
    if (!token || !serializedUser) return null;
    try {
      const user = JSON.parse(serializedUser) as AuthStorageUser;
      if (!user.id || !user.name || !user.email) return null;
      return {
        token,
        user,
        activeWorkspaceId: localStorage.getItem(ACTIVE_WORKSPACE_KEY),
      };
    } catch {
      return null;
    }
  },
  async save(session) {
    localStorage.setItem(TOKEN_KEY, session.token);
    localStorage.setItem(USER_KEY, JSON.stringify(session.user));
    if (session.activeWorkspaceId) {
      localStorage.setItem(ACTIVE_WORKSPACE_KEY, session.activeWorkspaceId);
    } else {
      localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    }
  },
  async setActiveWorkspaceId(activeWorkspaceId) {
    if (activeWorkspaceId) {
      localStorage.setItem(ACTIVE_WORKSPACE_KEY, activeWorkspaceId);
    } else {
      localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    }
  },
  async clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
  },
};

export const tauriAuthStorage: AuthStorage = {
  hydrate() {
    return invoke<AuthStorageSession | null>('get_auth_session');
  },
  save(session) {
    return invoke<void>('set_auth_session', { session });
  },
  setActiveWorkspaceId(activeWorkspaceId) {
    return invoke<void>('set_auth_active_workspace', { activeWorkspaceId });
  },
  clear(options = {}) {
    return invoke<void>('clear_auth_session', {
      deleteLocalActivityData: options.deleteLocalActivityData ?? false,
    });
  },
};

export function getAuthStorage(): AuthStorage {
  const isTauri =
    typeof window !== 'undefined' &&
    typeof (window as Window & { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__ !==
      'undefined';
  return isTauri ? tauriAuthStorage : webAuthStorage;
}

export const authStorage: AuthStorage = getAuthStorage();
